import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import useServerPagination from '@/hooks/useServerPagination';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { IRateData } from '@/providers/HttpProvider/plugins/EnvioFlex/types';
import { RootState } from '@/redux/store';
import { IResOrder, IResOrders } from '@/types/plugins/envioFlex';

import { Status } from './constants';

export interface IFilter {
  startDate: string;
  endDate: string;
  status: Status | null;
  [key: string]: string | Status | null | undefined;
}

export interface ChangeFilter {
  filterName: string;
  value: string | Status | null;
}

const initialFilters: IFilter = {
  startDate: '',
  endDate: '',
  status: null,
};

interface OrdersStatus {
  loading: boolean;
  error: boolean;
  data: IResOrders['data']['data'] | null;
  total: number;
}

export interface ModalState {
  coverage: boolean;
  confirmOrder: boolean;
  downloadLabel: boolean;
  confirmOrders: boolean;
  details: boolean;
}

const initialModals: ModalState = {
  coverage: false,
  confirmOrder: false,
  downloadLabel: false,
  confirmOrders: false,
  details: false,
};

const useEnvioFlex = () => {
  const pagination = useServerPagination();
  const { data } = useSelector((state: RootState) => state.parcel.profile);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<IFilter>(initialFilters);
  const [modals, setModals] = useState<ModalState>(initialModals);
  const [searchValue, setSearchValue] = useState<string>('');
  const [orders, setOrders] = useState<OrdersStatus>({
    loading: false,
    error: false,
    data: null,
    total: 0,
  });
  const [failedOrders, setFailedOrders] = useState<string[]>([]);
  const [successTrackingIds, setSuccessTrackingIds] = useState<string[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [selectedOrderIdForCoverage, setSelectedOrderIdForCoverage] = useState<string>('');
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<string>('');
  const [selectedTrackingIds, setSelectedTrackingIds] = useState<string[]>([]);
  const [rateData, setRateData] = useState<IRateData | null>(null);
  const [loadingRate, setLoadingRate] = useState<boolean>(false);

  const clientId = data?.credentials.clientId;

  const processPendingOrders = async () => {
    const ordersToProcess = orders.data || [];

    const pendingOrders = ordersToProcess.filter(
      (order) => order.status === 'pending' && !order.trackingId,
    );

    if (pendingOrders.length === 0) {
      setFailedOrders([]);
      setSuccessTrackingIds([]);
      handleModal('confirmOrders');
      return;
    }

    if (!clientId) {
      toast.error('No se puede procesar en este momento. Intenta recargar la página');
      setFailedOrders(pendingOrders.map((order) => order.orderId));
      handleModal('confirmOrders');
      return;
    }

    const http = initializeHttpProvider();
    const orderIds = pendingOrders.map((order) => order.id);

    let failedFromApi: string[] = [];
    try {
      const response = await http.envioFlex.processOrders({ clientId, orderIds });
      failedFromApi = (response && 'failedOrderIds' in response && response.failedOrderIds) || [];
      setFailedOrders(failedFromApi);
    } catch {
      toast.error('Error al procesar las órdenes. Por favor intenta nuevamente');
      setFailedOrders(orderIds);
      setSuccessTrackingIds([]);
      handleModal('confirmOrders');
      return;
    }

    const updatedOrders = await fetchOrders();

    if (updatedOrders) {
      const successfulOrders = updatedOrders.filter(
        (order) =>
          orderIds.includes(order.orderId) &&
          !failedFromApi.includes(order.orderId) &&
          order.trackingId,
      );
      const trackingIds = successfulOrders.map((order) => order.trackingId);
      setSuccessTrackingIds(trackingIds);
    } else {
      setSuccessTrackingIds([]);
    }

    handleModal('confirmOrders');
  };

  const onChangeFilter = useCallback(
    (filterName: keyof IFilter, value: string | Status | null) => {
      setFilters({ ...filters, [filterName]: value });
    },
    [filters, setFilters],
  );

  const clearFilters = () => {
    pagination.resetFilters();
    setFilters(initialFilters);
    setSearchValue('');
    setOpenFilters(false);
    fetchOrders(initialFilters);
  };

  const onClose = () => {
    setOpenFilters(false);
  };

  const applyFilters = () => {
    setOpenFilters(false);
    fetchOrders(filters);
  };

  const handleSearch = async () => {
    const orderId = searchValue.trim();
    if (!orderId) {
      fetchOrders();
      return;
    }
    if (!clientId) return;
    setOrders((prev) => ({ ...prev, loading: true, error: false }));
    const http = initializeHttpProvider();
    try {
      const res = await http.envioFlex.order({ orderId, clientId });
      if ('data' in res && res.success) {
        const list = (res as IResOrder).data;
        setOrders({
          data: Array.isArray(list) ? list : [list],
          total: Array.isArray(list) ? list.length : 1,
          loading: false,
          error: false,
        });
      } else {
        setOrders({ data: [], total: 0, loading: false, error: true });
        toast.error('Orden no encontrada');
      }
    } catch {
      setOrders((prev) => ({ ...prev, loading: false, error: true }));
      toast.error('Error al buscar la orden');
    }
  };

  const handleModal = (modalName: keyof ModalState) => {
    setModals((prev) => ({
      ...prev,
      [modalName]: !prev[modalName],
    }));
  };

  const handleRowClick = (orderId: string) => {
    setSelectedOrderId(orderId);
    handleModal('details');
  };

  const handleCoverageCheck = async (id: string) => {
    if (!clientId) {
      toast.error('No se puede validar cobertura. Recarga la página e intenta nuevamente');
      return;
    }

    const order = orders.data?.find((o) => o.id === id);
    const orderStatus = order?.status || '';
    setSelectedOrderStatus(orderStatus);

    setSelectedOrderIdForCoverage(id);
    setLoadingRate(true);
    handleModal('coverage');

    const http = initializeHttpProvider();
    try {
      const response = await http.envioFlex.validateRate({ clientId, orderId: id });
      if ('data' in response && response.data && 'prices' in response.data) {
        setRateData(response.data);
      } else {
        toast.error('No se pudo validar la cobertura. Por favor intenta más tarde');
        setRateData(null);
      }
    } catch {
      toast.error('No se pudo validar la cobertura. Por favor intenta más tarde');
      setRateData(null);
    } finally {
      setLoadingRate(false);
    }
  };

  const createOrderFromRate = async () => {
    if (!clientId || !selectedOrderIdForCoverage) {
      toast.error('No se puede crear la orden. Intenta nuevamente');
      return;
    }

    const http = initializeHttpProvider();
    try {
      const response = await http.envioFlex.createOrder({
        clientId,
        orderId: selectedOrderIdForCoverage,
      });
      if ('success' in response && response.success) {
        toast.success('Orden creada exitosamente');
        fetchOrders();
      } else {
        toast.error('Error al crear la orden. Por favor intenta más tarde');
      }
    } catch {
      toast.error('Error al crear la orden. Por favor intenta más tarde');
    }
  };

  const handleDownloadLabel = (trackingId: string) => {
    setSelectedTrackingIds([trackingId]);
    handleModal('downloadLabel');
  };

  const downloadLabels = async ({
    pageSize,
    trackingIds,
  }: {
    pageSize: 'small' | 'letter' | 'zebra';
    trackingIds: string[];
  }) => {
    const http = initializeHttpProvider();
    try {
      const response = await http.orders.labels({
        pageSize,
        trackingIds,
      });
      if ('data' in response && !Array.isArray(response.data) && response.data.url) {
        window.open(response.data.url, '_blank');
        toast.success('Etiqueta descargada');
        handleModal('downloadLabel');
      } else {
        toast.error('Error al descargar la etiqueta');
      }
    } catch {
      toast.error('Ha ocurrido un error al intentar descargar la etiqueta');
    }
  };

  const fetchOrders = async (activeFilters?: IFilter) => {
    if (!clientId) return null;
    const f = activeFilters ?? filters;

    setOrders((prev) => ({ ...prev, loading: true }));
    const http = initializeHttpProvider();
    try {
      const response = await http.envioFlex.orders({
        page: pagination.filters.page,
        pageSize: pagination.filters.page_size,
        clientId: clientId,
        ...(f.status && { status: f.status }),
        ...(f.startDate && { dateFrom: f.startDate }),
        ...(f.endDate && { dateTo: f.endDate }),
      });
      const ordersData = (response as IResOrders).data.data;
      setOrders({
        loading: false,
        error: false,
        data: ordersData,
        total: (response as IResOrders).data.total,
      });
      return ordersData;
    } catch {
      toast.error('Error al cargar las órdenes. Por favor recarga la página');
      setOrders({ loading: false, error: true, data: null, total: 0 });
      return null;
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [pagination.filters.page, pagination.filters.page_size, clientId]);

  const filteredOrders = useMemo(() => orders.data || [], [orders.data]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  return {
    pagination,
    onChangeFilter,
    filters,
    setOpenFilters,
    openFilters,
    clearFilters,
    onClose,
    applyFilters,
    orders: {
      ...orders,
      data: filteredOrders,
    },
    handleModal,
    modals,
    processPendingOrders,
    failedOrders,
    successTrackingIds,
    selectedOrderId,
    handleRowClick,
    clientId,
    searchValue,
    handleSearchChange,
    handleSearch,
    rateData,
    loadingRate,
    handleCoverageCheck,
    createOrderFromRate,
    selectedOrderIdForCoverage,
    selectedOrderStatus,
    selectedTrackingIds,
    handleDownloadLabel,
    downloadLabels,
  };
};

export default useEnvioFlex;
